/**
 * Alpine.js component for managing product selling plan options (Skio integration)
 * Handles reactive updates when variants change and manages selling plan selection
 */

(function() {
  'use strict';
  
  function registerComponent() {
    if (typeof Alpine === 'undefined') {
      console.error('Alpine.js is not loaded');
      return;
    }
    
    if (Alpine.data('productSellingPlanOptions')) {
      return;
    }
    Alpine.data('productSellingPlanOptions', (options = {}) => {
      const {
        productDataScriptSelector = '[data-product-with-selling-plans]',
        productFormId = null,
        savingsTextTemplate = null
      } = options;

      return {
        selectedVariant: null,
        selectedSellingPlanGroupId: 'one-time-purchase',
        selectedSellingPlanId: null,
        selectedVariantSellingPlanGroups: [],
        selectedVariantAllocations: {},
        preselectedAllocations: {},
        preselectedAllocationsDiscounts: {},
        preselectedSellingPlanIds: {},
        selectedVariantHasAllocations: false,

        init() {
          // ── FIX 5: Guard against double-init ──────────────────────────
          // Alpine's MutationObserver can re-init this component when it
          // detects attribute changes from morph / x-data patching.  The
          // mutateDom() wrapper in quick-add.js prevents this, but as an
          // extra safety net we mark the element so a stale init is a no-op.
          if (this.$el && this.$el.__sellingPlanInitialized) return;
          if (this.$el) this.$el.__sellingPlanInitialized = true;

          // ── FIX 3: Do NOT add manual window listeners ─────────────────
          // The Liquid template already declares:
          //   x-on:variant:update.window="handleSelectedVariantChange"
          //   x-on:variant-change.window="handleSelectedVariantChange"
          // Adding window.addEventListener duplicates every call, and the
          // second invocation resets the reactive state Alpine set up during
          // the first call.  Removing them ensures a single invocation.

          // Initialize with current variant after a short delay to ensure DOM is ready
          this.$nextTick(() => {
            this.handleSelectedVariantChange();
          });
          
          // Hook into form submission to sync selling_plan to form
          // Horizon theme uses FormData which doesn't include inputs with 'form' attribute
          // We need to sync BEFORE FormData is created, so use capture phase with high priority
          // Find form dynamically since productFormId might be wrong
          const findForm = () => {
            // Try multiple methods to find the form
            let form = null;
            if (productFormId) {
              form = document.getElementById(productFormId);
            }
            if (!form) {
              form = document.querySelector('form[data-type="add-to-cart-form"]');
            }
            if (!form && this.$el) {
              const section = this.$el.closest('[data-section-id]');
              if (section) {
                form = section.querySelector('form[data-type="add-to-cart-form"]');
              }
            }
            return form;
          };
          
          // Try to find form immediately
          let form = findForm();
          
          // If form not found, wait a bit and try again (form might not be rendered yet)
          if (!form) {
            this.$nextTick(() => {
              form = findForm();
              if (form) {
                this.setupFormListeners(form);
              }
            });
          } else {
            this.setupFormListeners(form);
          }
          
          // Also try after a delay in case form loads later
          setTimeout(() => {
            if (!form) {
              form = findForm();
              if (form) {
                this.setupFormListeners(form);
              }
            }
          }, 500);
          
          // Watch for selling plan frequency changes
          this.$watch('selectedSellingPlanGroupId', (groupId) => {
            if (groupId && groupId !== 'one-time-purchase') {
              // Use $nextTick to ensure DOM is updated and preselectedSellingPlanIds might be set by radio buttons
              this.$nextTick(() => {
                let planId = this.preselectedSellingPlanIds[groupId];
                
                // If no planId yet, resolve from first allocation (FIX 4)
                if (!planId) {
                  const allocations = this.selectedVariantAllocations[groupId];
                  if (allocations && allocations.length > 0) {
                    const numPlanId = this._resolvePlanId(allocations[0]);
                    if (numPlanId) {
                      planId = numPlanId;
                      this.preselectedSellingPlanIds[groupId] = numPlanId;
                    }
                  }
                }
                
                // Set selectedSellingPlanId if we have a planId
                if (planId) {
                  this.selectedSellingPlanId = planId;
                  this.syncSellingPlanToForm();
                }
              });
            } else {
              // One-time purchase - clear selling plan
              this.selectedSellingPlanId = null;
              this.syncSellingPlanToForm();
            }
          });
          
          // Watch for preselectedSellingPlanIds changes to update selectedSellingPlanId
          this.$watch('preselectedSellingPlanIds', (newVal, oldVal) => {
            if (this.selectedSellingPlanGroupId !== 'one-time-purchase') {
              const planId = this.preselectedSellingPlanIds[this.selectedSellingPlanGroupId];
              if (planId && planId !== this.selectedSellingPlanId) {
                this.selectedSellingPlanId = planId;
                this.syncSellingPlanToForm();
              }
            }
          }, { deep: true });
          
          // Watch for selectedSellingPlanId changes
          this.$watch('selectedSellingPlanId', () => {
            this.syncSellingPlanToForm();
          });
        },
        
        destroy() {
          // Clean up the guard flag so the component can be re-initialized
          // properly if the modal is re-opened with fresh content.
          if (this.$el) this.$el.__sellingPlanInitialized = false;
        },

        // ── FIX 4: Robust selling plan ID resolution ──────────────────
        // Different Shopify API / Liquid serialisation formats put the plan
        // ID in different places.  This helper checks all known locations.
        _resolvePlanId(allocation) {
          if (!allocation) return null;
          // Nested selling_plan / sellingPlan object
          const plan = allocation.selling_plan || allocation.sellingPlan;
          if (plan && plan.id != null) {
            return typeof plan.id === 'string' ? parseInt(plan.id, 10) : plan.id;
          }
          // Top-level selling_plan_id / sellingPlanId (Storefront API format)
          const directId = allocation.selling_plan_id ?? allocation.sellingPlanId;
          if (directId != null) {
            return typeof directId === 'string' ? parseInt(directId, 10) : directId;
          }
          return null;
        },

        setupFormListeners(form) {
          const syncFn = () => {
            this.syncSellingPlanToForm();
          };
          
          // Use capture phase with high priority to run before preventDefault and FormData creation
          form.addEventListener('submit', syncFn, { capture: true, passive: false });
          // Listen for the explicit sync event dispatched by product-form.js
          // right before FormData is created (safety net for Quick Add).
          form.addEventListener('selling-plan:sync', syncFn);
          
          // Also sync on any button click that might trigger form submission
          const submitButtons = form.querySelectorAll('button[type="submit"]');
          submitButtons.forEach(button => {
            button.addEventListener('click', syncFn, { capture: true });
          });
          
          // Also sync on mousedown to catch clicks before submit
          submitButtons.forEach(button => {
            button.addEventListener('mousedown', syncFn, { capture: true });
          });
        },
        
        syncSellingPlanToForm() {
          // Find the form dynamically - try multiple methods
          let form = null;

          // FIRST: if we're inside the quick-add modal, scope lookup to the modal only.
          // This prevents grabbing a form from a different quick-add card or the PDP.
          if (this.$el) {
            const modal = this.$el.closest('#quick-add-modal-content');
            if (modal) {
              form = modal.querySelector('form[data-type="add-to-cart-form"]');
            }
          }
          
          // Try the passed productFormId (already patched to the modal form ID when in modal)
          if (!form && productFormId) {
            form = document.getElementById(productFormId);
          }
          
          // If still not found, try to find form closest to the selling plan selection
          if (!form && this.$el) {
            const sellingPlanContainer = this.$el.closest('.selling-plan-selection');
            if (sellingPlanContainer) {
              // Look for form in the same section
              const section = sellingPlanContainer.closest('[data-section-id]');
              if (section) {
                form = section.querySelector('form[data-type="add-to-cart-form"]');
              }
            }
          }

          // If still not found, try to find form within product-form-component
          if (!form) {
            const productFormComponent = document.querySelector('product-form-component');
            if (productFormComponent) {
              form = productFormComponent.querySelector('form');
            }
          }
          
          // Last resort: first matching form on the page
          if (!form) {
            form = document.querySelector('form[data-type="add-to-cart-form"]');
          }
          
          if (!form) {
            console.warn('syncSellingPlanToForm: Form not found. Tried:', productFormId);
            return;
          }
          
          // Get the current selling plan value - check preselectedSellingPlanIds first for the selected group
          let sellingPlanValue = null;
          if (this.selectedSellingPlanGroupId !== 'one-time-purchase') {
            // Priority: selectedSellingPlanId > preselectedSellingPlanIds[groupId]
            sellingPlanValue = this.selectedSellingPlanId || this.preselectedSellingPlanIds[this.selectedSellingPlanGroupId] || null;
          }
          
          
          // Remove ALL selling_plan inputs first (both inside form and outside with form attribute)
          const allSellingPlanInputs = document.querySelectorAll('input[name="selling_plan"]');
          allSellingPlanInputs.forEach(input => {
            // Remove inputs that are outside the form (have form attribute)
            if (input.hasAttribute('form') && input.getAttribute('form') !== null) {
              input.remove();
            }
            // Also remove inputs inside the form if we're doing one-time purchase
            if (!sellingPlanValue && form.contains(input)) {
              input.remove();
            }
          });
          
          // Only add selling_plan input if we have a subscription plan selected
          if (sellingPlanValue) {
            let formSellingPlanInput = form.querySelector('input[name="selling_plan"]');
            // Add or update selling_plan input in form
            if (!formSellingPlanInput) {
              formSellingPlanInput = document.createElement('input');
              formSellingPlanInput.type = 'hidden';
              formSellingPlanInput.name = 'selling_plan';
              form.appendChild(formSellingPlanInput);
            }
            formSellingPlanInput.value = String(sellingPlanValue);
          }
        },

        handleSelectedVariantChange(event) {
          // Get product data from script tag or from event
          let variant = null;
          
          if (event && event.detail && event.detail.resource) {
            // Variant data from variant change event (Horizon theme structure)
            variant = event.detail.resource;
            this.selectedVariant = variant;
          } else {
            // Fallback: get current variant from form or product data
            const form = productFormId ? document.getElementById(productFormId) : (this.$el ? this.$el.closest('form') : null);
            
            let variantId = null;
            if (form) {
              const variantIdInput = form.querySelector('input[name="id"]');
              if (variantIdInput) {
                variantId = variantIdInput.value;
              }
            }
            
            // Try to find variant in product data (check both selectors)
            const productScript = document.querySelector(productDataScriptSelector) || document.querySelector('[data-product-json]');
            if (productScript) {
              try {
                const product = JSON.parse(productScript.textContent);
                if (variantId) {
                  variant = product.variants.find(v => v.id == variantId || v.id == parseInt(variantId));
                } else {
                  // Use selected or first available variant
                  variant = product.selected_or_first_available_variant || product.variants[0];
                }
                if (variant) {
                  this.selectedVariant = variant;
                }
              } catch (e) {
                // Silent fail
              }
            }
          }

          if (!variant) {
            return;
          }

          this.selectedVariant = {
            ...variant,
            priceFormatted: this.formatMoney(variant.price)
          };

          // Get selling plan allocations
          // Variant data might have selling_plan_allocations directly or need to fetch
          let allocations = variant.selling_plan_allocations || variant.sellingPlanAllocations?.nodes || [];
          
          // If no allocations in variant, try to get from product data
          if (!allocations || allocations.length === 0) {
            const productScript = document.querySelector(productDataScriptSelector) || document.querySelector('[data-product-json]');
            if (productScript) {
              try {
                const product = JSON.parse(productScript.textContent);
                const fullVariant = product.variants.find(v => v.id == variant.id);
                if (fullVariant) {
                  allocations = fullVariant.selling_plan_allocations || fullVariant.sellingPlanAllocations?.nodes || [];
                }
              } catch (e) {
                // Silent fail
              }
            }
          }
          
          // Group allocations by selling plan group
          const groupsMap = new Map();
          allocations.forEach(allocation => {
            // Handle both formats: allocation.selling_plan_group_id or allocation.sellingPlanGroupId
            const groupId = allocation.selling_plan_group_id || allocation.sellingPlanGroupId;
            if (!groupId) return;
            
            if (!groupsMap.has(groupId)) {
              groupsMap.set(groupId, {
                id: groupId,
                allocations: []
              });
            }
            groupsMap.get(groupId).allocations.push(allocation);
          });

          this.selectedVariantSellingPlanGroups = Array.from(groupsMap.values());
          this.selectedVariantHasAllocations = allocations.length > 0;

          // Process allocations for each group
          this.selectedVariantAllocations = {};
          this.preselectedAllocations = {};
          this.preselectedAllocationsDiscounts = {};

          // Get plan names mapping from block settings if available
          const planNamesMapping = {};
          const planNamesMappingText = document.querySelector('[data-plan-names-mapping]')?.textContent;
          if (planNamesMappingText) {
            try {
              const mapping = JSON.parse(planNamesMappingText);
              Object.assign(planNamesMapping, mapping);
            } catch (e) {
              // Parse as text format: "Every 45 Days:45 days"
              const lines = planNamesMappingText.split('\n');
              lines.forEach(line => {
                const parts = line.split(':');
                if (parts.length === 2) {
                  planNamesMapping[parts[0].trim()] = parts[1].trim();
                }
              });
            }
          }
          
          groupsMap.forEach((group, groupId) => {
            // Process allocations and add planPickerName
            const processedAllocations = group.allocations.map(allocation => {
              const plan = allocation.selling_plan || allocation.sellingPlan;
              if (plan) {
                const planOptionValue = plan.options?.[0]?.value || plan.name;
                const planPickerName = planNamesMapping[planOptionValue] || planOptionValue || plan.name;
                return {
                  ...allocation,
                  planPickerName: planPickerName
                };
              }
              return allocation;
            });
            
            this.selectedVariantAllocations[groupId] = processedAllocations;
            
            // Get first allocation for pricing
            const firstAllocation = group.allocations[0];
            if (!firstAllocation) return;
            
            // Handle both formats: selling_plan or sellingPlan
            const plan = firstAllocation.selling_plan || firstAllocation.sellingPlan;
            if (!plan) return;
            
            // Handle both formats: price_adjustments or priceAdjustments
            const priceAdjustments = plan.price_adjustments || plan.priceAdjustments || [];
            const priceAdjustment = priceAdjustments[0];
            
            let price = variant.price;
            let compareAtPrice = variant.compare_at_price || variant.price;
            let discountValue = '';
            let discountAmount = 0;
            let discountPercent = 0;

            if (priceAdjustment) {
              switch (priceAdjustment.value_type) {
                case 'percentage':
                  discountPercent = priceAdjustment.value;
                  discountAmount = (variant.price * discountPercent) / 100;
                  discountValue = `${discountPercent}%`;
                  price = variant.price - discountAmount;
                  break;
                case 'fixed_amount':
                  discountAmount = priceAdjustment.value;
                  discountPercent = Math.round((discountAmount / variant.price) * 100);
                  discountValue = this.formatMoney(discountAmount);
                  price = variant.price - discountAmount;
                  break;
                case 'price':
                  price = priceAdjustment.value;
                  discountAmount = variant.price - price;
                  discountPercent = Math.round((discountAmount / variant.price) * 100);
                  discountValue = `${discountPercent}%`;
                  break;
              }
            }

            this.preselectedAllocations[groupId] = {
              price: price,
              compare_at_price: variant.price, // Original variant price for strikethrough
              priceFormatted: this.formatMoney(price),
              compareAtPriceFormatted: this.formatMoney(variant.price)
            };

            // Calculate savings text - use template if provided, otherwise default
            let savingsText;
            if (savingsTextTemplate) {
              savingsText = this.formatSavingsText(
                savingsTextTemplate,
                this.formatMoney(discountAmount),
                discountPercent
              );
            } else {
              savingsText = `Get ${this.formatMoney(discountAmount)} off your first order—and enjoy ${discountPercent}% off all future purchases`;
            }
            
            this.preselectedAllocationsDiscounts[groupId] = {
              value: discountValue,
              amountOff: this.formatMoney(discountAmount),
              savingsText: savingsText,
              discountPercent: discountPercent
            };

            // Set default selected plan for group (FIX 4: use _resolvePlanId)
            const resolvedPlanId = this._resolvePlanId(firstAllocation);
            if (resolvedPlanId && !this.preselectedSellingPlanIds[groupId]) {
              this.preselectedSellingPlanIds[groupId] = resolvedPlanId;
              // If this is the selected group, also set selectedSellingPlanId
              if (this.selectedSellingPlanGroupId === groupId) {
                this.selectedSellingPlanId = resolvedPlanId;
              }
            }
          });

          // Auto-select first group if none selected and variant has allocations
          if (this.selectedVariantHasAllocations && this.selectedSellingPlanGroupId === 'one-time-purchase') {
            const firstGroup = this.selectedVariantSellingPlanGroups[0];
            if (firstGroup) {
              this.selectedSellingPlanGroupId = firstGroup.id;
              // FIX 4: use _resolvePlanId for robust plan ID resolution
              const planId = this._resolvePlanId(firstGroup.allocations[0]);
              if (planId) {
                this.selectedSellingPlanId = planId;
                this.preselectedSellingPlanIds[firstGroup.id] = planId;
              }
            }
          } else if (!this.selectedVariantHasAllocations) {
            this.selectedSellingPlanGroupId = 'one-time-purchase';
            this.selectedSellingPlanId = null;
          }
          
          this.syncSellingPlanToForm();
        },

        handleSelectSellingPlanGroup(groupId) {
          this.selectedSellingPlanGroupId = groupId;
          
          if (groupId !== 'one-time-purchase') {
            // Use $nextTick to ensure radio buttons have updated preselectedSellingPlanIds
            this.$nextTick(() => {
              let planId = this.preselectedSellingPlanIds[groupId];
              
              // If no planId yet, resolve from first allocation (FIX 4)
              if (!planId) {
                const allocations = this.selectedVariantAllocations[groupId];
                if (allocations && allocations.length > 0) {
                  const numPlanId = this._resolvePlanId(allocations[0]);
                  if (numPlanId) {
                    planId = numPlanId;
                    this.preselectedSellingPlanIds[groupId] = numPlanId;
                  }
                }
              }
              
              // Set selectedSellingPlanId if we have a planId
              if (planId) {
                this.selectedSellingPlanId = planId;
                this.syncSellingPlanToForm();
              }
            });
          } else {
            this.selectedSellingPlanId = null;
            this.syncSellingPlanToForm();
          }
        },

        formatMoney(cents) {
          if (typeof cents !== 'number') {
            cents = parseFloat(cents) || 0;
          }
          
          // Use Shopify's money format if available
          if (window.Shopify && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(cents);
          }
          
          // Fallback formatting
          const dollars = (cents / 100).toFixed(2);
          return `$${dollars}`;
        },

        formatSavingsText(template, amount, percent) {
          if (!template) return '';
          return template
            .replace(/\{\{amount\}\}/g, amount || '')
            .replace(/\{\{percent\}\}/g, percent || '');
        }
      };
    });
  }
  
  // Register immediately if Alpine is already available
  if (typeof window.Alpine !== 'undefined' && window.Alpine.data) {
    registerComponent();
  } else {
    // Wait for Alpine to initialize
    document.addEventListener('alpine:init', function() {
      registerComponent();
    });
    
    // Also check if Alpine loads before alpine:init fires (for CDN loading)
    let checkCount = 0;
    const checkAlpine = setInterval(function() {
      checkCount++;
      if (typeof window.Alpine !== 'undefined' && window.Alpine.data) {
        registerComponent();
        clearInterval(checkAlpine);
      } else if (checkCount > 100) {
        // Stop checking after 10 seconds
        clearInterval(checkAlpine);
        console.warn('Alpine.js not found after 10 seconds');
      }
    }, 100);
  }
})();
